import {
  FunnelStepHeader,
  FunnelWithHelper,
  StepperFooter,
  useStepperContext,
} from '@folio/components';
import { Card, CardContent, useToast } from '@folio/components/ui';
import { Button } from '@folio/components/ui/button';
import { DocumentViewer, useDocumentViewer } from '@folio/components/features/document-viewer';
import type { DocumentViewerDocument } from '@folio/components/features/document-viewer';
import { DocumentsCollection } from '@folio/components/module-elements/Documents/DocumentsCollection';
import { getUploadedDocumentGroups } from '@folio/components/module-elements/Documents/uploaded-document-groups';
import { translateBrokerageFirmDocument } from '@folio/utils/getDocumentCategoryLabel';
import { LoadingCard } from '@folio/components/ui/loading-card/loading-card';
import { useAssertedParams } from '@folio/lib/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  brokerageFirmControllerListDocumentsQueryOptions,
  configControllerGetPublicProductConfigQueryOptions,
  configControllerGetPublicTenantConfigQueryOptions,
  keyValueFieldsToObject,
  policyControllerGetPolicyQueryOptions,
  policyControllerListPolicyDocumentsQueryOptions,
  uploadDocumentsOnBrokerageFirm,
  uploadDocumentsOnPolicy,
  useBrokerageFirmControllerDeleteDocuments,
  usePolicyControllerDeletePolicyDocuments,
} from 'korint-sdk';
import {
  useDocumentsAutoClassification,
  type ClassificationStatus,
  type DocumentClassificationState,
} from 'korint-sdk/react';
import {
  ArrowRight,
  CircleAlert,
  CircleCheck,
  FileText,
  Info,
  Loader2,
  Upload,
  X,
  Zap,
} from 'lucide-react';
import posthog from 'posthog-js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mutate as swrMutate } from 'swr';
import { Group, Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { Alert, AlertDescription } from '@folio/components/ui/alert';
import { ThemeIcon } from '@folio/components/ui/theme-icon';

type DocumentUploadFunnelModuleProps = {
  context: 'policy' | 'brokerageFirm';
  displayContext?: 'broker' | 'policyholder';
  helper?: React.ReactNode;
  hiddenDocumentCategories?: string[];
};

type PrefillChoice = 'upload' | 'later';

// Keepalives count as activity while the backend is still processing a document.
const CLASSIFICATION_STALE_TIMEOUT_MS = 45_000;

const STAGE_LABEL_KEYS: Partial<Record<ClassificationStatus, string>> = {
  validating: 'documents.prefill.stage.validating',
  scanning: 'documents.prefill.stage.scanning',
  scan_complete: 'documents.prefill.stage.scan_complete',
  classifying: 'documents.prefill.stage.classifying',
  classified: 'documents.prefill.stage.classified',
  extracting: 'documents.prefill.stage.extracting',
  checking_validity: 'documents.prefill.stage.checking_validity',
  completed: 'documents.prefill.stage.completed',
  error: 'documents.prefill.stage.error',
};

// Normalize category strings for comparison — the classifier occasionally returns
// slight variations in casing/spacing vs. what's declared in product configs, so we
// compare normalized forms to keep the checklist in sync with per-file classification.
function normalizeCategory(category: string): string {
  return category.trim().toLowerCase();
}

function getMatchedCategoriesFromSSE(
  classificationStates: Map<string, DocumentClassificationState>,
): Set<string> {
  const matched = new Set<string>();
  for (const state of classificationStates.values()) {
    if (state.result?.category) {
      matched.add(normalizeCategory(state.result.category));
    }
  }
  return matched;
}

function DocumentProcessingItem({
  fileName,
  state,
  onRemove,
}: {
  fileName: string;
  state: DocumentClassificationState;
  onRemove?: () => void;
}) {
  const { t } = useTranslation();
  const isDone = state.status === 'completed';
  const isError = state.status === 'error' || state.status === 'scan_infected';
  const isMatched = isDone && Boolean(state.result?.category);
  const labelKey = STAGE_LABEL_KEYS[state.status];
  const label = labelKey ? t(labelKey) : state.status;

  return (
    <Group justify="between" gap={2} className="rounded-md border bg-muted/20 px-3 py-2 text-sm">
      <Group gap={2} className="min-w-0">
        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
        <Text inline truncate>
          {fileName}
        </Text>
      </Group>
      <Group gap={1} className="shrink-0">
        {isMatched ? (
          <CircleCheck className="h-4 w-4 text-green-600" />
        ) : isDone ? (
          <CircleCheck className="h-4 w-4 text-muted-foreground" />
        ) : isError ? (
          <>
            <Text inline size="xs" tone="destructive">
              {label}
            </Text>
            <CircleAlert className="h-4 w-4 text-destructive" />
          </>
        ) : (
          <Loader2 className="h-4 w-4 text-primary animate-spin" />
        )}
        {isError && onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-foreground"
            onClick={onRemove}
            aria-label={t('documents.prefill.remove')}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </Group>
    </Group>
  );
}

function ChoiceCard({
  icon,
  iconVariant,
  title,
  description,
  footer,
  selected,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  iconVariant: 'primary' | 'muted';
  title: string;
  description: string;
  footer?: React.ReactNode;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-start gap-3 text-left rounded-lg border-2 bg-white p-5 transition-colors ${
        selected ? 'border-primary bg-primary/5' : 'border-transparent hover:border-muted'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <ThemeIcon radius="md" tone={iconVariant}>
        {icon}
      </ThemeIcon>
      <Stack gap={2} className="flex-1 min-w-0">
        <Group align="start" justify="between" gap={3}>
          <Stack gap={1}>
            <div className="font-medium">{title}</div>
            <div className="text-sm text-muted-foreground">{description}</div>
          </Stack>
          <div
            className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
              selected ? 'border-primary' : 'border-muted-foreground/40'
            }`}
          >
            {selected && <div className="h-2 w-2 rounded-full bg-primary" />}
          </div>
        </Group>
        {footer}
      </Stack>
    </button>
  );
}

const selectDocuments = (data: {
  data: {
    id: string;
    name: string;
    signedUrl: string;
    createdAt: string;
    type: DocumentViewerDocument['type'];
    category: string | null;
    metadata: { key: string; value: string }[];
  }[];
}) =>
  data.data.map(doc => {
    const metadata = keyValueFieldsToObject(doc.metadata) as Record<
      string,
      string | number | boolean | undefined
    >;
    return {
      id: doc.id,
      name: doc.name,
      signedUrl: doc.signedUrl || '',
      createdAt: doc.createdAt,
      metadata,
      type: doc.type,
      category: doc.category,
    };
  });

export function DocumentUploadFunnelModule({
  context,
  displayContext = 'broker',
  helper,
  hiddenDocumentCategories,
}: DocumentUploadFunnelModuleProps) {
  const { t } = useTranslation();
  const stepper = useStepperContext();
  const { toast } = useToast();
  const [pendingCount, setPendingCount] = useState(0);
  const [allComplete, setAllComplete] = useState(false);
  const [choice, setChoice] = useState<PrefillChoice>('upload');
  const choiceInitializedRef = useRef(false);
  const fileNamesRef = useRef<Map<string, string>>(new Map());

  const params = useAssertedParams<{
    policyId?: string;
    branchId?: string;
    brokerageFirmId?: string;
  }>();

  const { data: productId } = useQuery({
    ...policyControllerGetPolicyQueryOptions(params.policyId!, { branchId: params.branchId }),
    enabled: context === 'policy' && !!params.policyId,
    select: data => data.productId,
  });

  const { data: productConfig, isLoading: isConfigLoading } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId!),
    enabled: !!productId,
  });

  const { data: tenantConfig, isLoading: isTenantConfigLoading } = useQuery({
    ...configControllerGetPublicTenantConfigQueryOptions(),
  });

  useEffect(() => {
    if (choiceInitializedRef.current || !tenantConfig) return;
    // The `documentPrefill` fields are optional on the backend DTO — older deployments
    // that haven't rolled it out, and tenants that don't configure the per-context
    // field, simply get the 'upload' fallback.
    const prefillConfig = (
      tenantConfig as typeof tenantConfig & {
        documentPrefill?: {
          policyholderDefaultChoice?: PrefillChoice;
          brokerageFirmDefaultChoice?: PrefillChoice;
        };
      }
    ).documentPrefill;
    const tenantDefault =
      context === 'policy'
        ? prefillConfig?.policyholderDefaultChoice
        : prefillConfig?.brokerageFirmDefaultChoice;
    if (tenantDefault === 'later' || tenantDefault === 'upload') {
      setChoice(tenantDefault);
    }
    choiceInitializedRef.current = true;
  }, [tenantConfig, context]);

  const brokerageFirmDocumentsList =
    context === 'brokerageFirm'
      ? tenantConfig?.brokerageFirmDocuments?.brokerageFirmDocumentsList
      : undefined;

  const { data: policyDocuments } = useQuery({
    ...policyControllerListPolicyDocumentsQueryOptions(params.policyId!, {
      branchId: params.branchId,
    }),
    enabled: context === 'policy' && !!params.policyId,
    select: selectDocuments,
  });

  const { data: brokerageFirmDocuments } = useQuery({
    ...brokerageFirmControllerListDocumentsQueryOptions(params.brokerageFirmId!),
    enabled: context === 'brokerageFirm' && !!params.brokerageFirmId,
    select: selectDocuments,
  });

  const rawDocuments = context === 'policy' ? policyDocuments : brokerageFirmDocuments;
  const existingDocuments = useMemo(
    () => (rawDocuments ?? []).filter(doc => doc.type === 'supporting_document'),
    [rawDocuments],
  );

  const existingDocCount = existingDocuments.length;
  const existingDocIds = useMemo(
    () => new Set(existingDocuments.map(d => d.id)),
    [existingDocuments],
  );

  const existingCategories = useMemo(() => {
    const cats = new Set<string>();
    for (const doc of existingDocuments ?? []) {
      if (!doc.category || doc.category === 'uncategorized') continue;
      cats.add(normalizeCategory(doc.category ?? ''));
    }
    return cats;
  }, [existingDocuments]);

  const viewerDocuments: DocumentViewerDocument[] = useMemo(
    () => existingDocuments,
    [existingDocuments],
  );

  const { selectedDocument, selectDocument, closeViewer } = useDocumentViewer(viewerDocuments);

  const documentTranslate = context === 'brokerageFirm' ? translateBrokerageFirmDocument(t) : t;

  const documentsLists = useMemo(() => {
    const source =
      context === 'brokerageFirm'
        ? (brokerageFirmDocumentsList ?? [])
        : (productConfig?.policyholderDocuments ?? []);
    const filtered = hiddenDocumentCategories
      ? source.filter(doc => !hiddenDocumentCategories.includes(doc.category))
      : source;
    return filtered.map(doc => ({
      category: doc.category,
      // A category helps prefill when the product/tenant config declares
      // extractionFields — those fields feed the subsequent form steps.
      prefillsForm: (doc.extractionFields?.length ?? 0) > 0,
    }));
  }, [context, productConfig, brokerageFirmDocumentsList, hiddenDocumentCategories]);

  const { prefillChips, otherChips } = useMemo(() => {
    const prefill: typeof documentsLists = [];
    const other: typeof documentsLists = [];
    for (const doc of documentsLists) {
      (doc.prefillsForm ? prefill : other).push(doc);
    }
    return { prefillChips: prefill, otherChips: other };
  }, [documentsLists]);

  const queryClient = useQueryClient();

  const { classificationStates, lastActivityAt, processDocuments, dismissDocument } =
    useDocumentsAutoClassification({
      onAllComplete: () => {
        setAllComplete(true);
        // Refresh the documents list so the category written server-side
        // during classification is reflected in `existingCategories` — otherwise the
        // top checklist can lag behind per-file completion state.
        if (context === 'policy' && params.policyId) {
          queryClient.invalidateQueries({
            queryKey: policyControllerListPolicyDocumentsQueryOptions(params.policyId, {
              branchId: params.branchId,
            }).queryKey,
          });
        }
        if (context === 'brokerageFirm' && params.brokerageFirmId) {
          queryClient.invalidateQueries({
            queryKey: brokerageFirmControllerListDocumentsQueryOptions(params.brokerageFirmId)
              .queryKey,
          });
        }
      },
    });

  const handleDismissDocument = useCallback(
    (key: string) => {
      dismissDocument(key);
      fileNamesRef.current.delete(key);
    },
    [dismissDocument],
  );

  const sseMatchedCategories = useMemo(
    () => getMatchedCategoriesFromSSE(classificationStates),
    [classificationStates],
  );

  const matchedCategories = useMemo(() => {
    const merged = new Set(existingCategories);
    for (const cat of sseMatchedCategories) {
      merged.add(cat);
    }
    return merged;
  }, [existingCategories, sseMatchedCategories]);

  const hasUploaded = existingDocCount > 0 || pendingCount > 0;
  const isProcessing = pendingCount > 0 && !allComplete;
  // Once the user has uploaded anything, lock them into the "upload" path — otherwise
  // they could switch to "later" and silently drop the work they just did.
  const effectiveChoice: PrefillChoice = hasUploaded ? 'upload' : choice;

  // Single-attempt retry on network failure — on cold review environments the
  // very first upload occasionally times out or fails auth before the token is
  // refreshed, so we retry once after a short backoff before surfacing an error.
  const uploadWithRetry = useCallback(async <T,>(attempt: () => Promise<T>): Promise<T> => {
    try {
      return await attempt();
    } catch (err) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return attempt();
    }
  }, []);

  // Prefill mutates form fields downstream of the upload step — only meaningful on the
  // first funnel step (where no values have been entered yet). On later steps the user
  // has already filled the form, and re-running prefill could overwrite intentional
  // edits (e.g. company name on the quote).
  const prefillEligible = stepper.isFirstStep ? 'true' : 'false';

  const uploadPolicy = useCallback(
    (files: File[]) =>
      Promise.all(
        files.map(file =>
          uploadWithRetry(() =>
            uploadDocumentsOnPolicy({
              policyId: params.policyId!,
              branchId: params.branchId,
              payload: {
                file,
                type: 'supporting_document',
                category: 'uncategorized',
                metadata: { prefillEligible },
                name: file.name,
              },
            }).then(result => ({ ...result, fileName: file.name })),
          ),
        ),
      ),
    [params.policyId, params.branchId, uploadWithRetry, prefillEligible],
  );

  const uploadBrokerageFirm = useCallback(
    (files: File[]) =>
      Promise.all(
        files.map(file =>
          uploadWithRetry(() =>
            uploadDocumentsOnBrokerageFirm({
              brokerageFirmId: params.brokerageFirmId!,
              payload: {
                file,
                type: 'supporting_document',
                category: 'uncategorized',
                metadata: { prefillEligible },
                name: file.name,
              },
            }).then(result => ({ ...result, fileName: file.name })),
          ),
        ),
      ),
    [params.brokerageFirmId, uploadWithRetry, prefillEligible],
  );

  const { mutateAsync: handleUpload, isPending: isUploading } = useMutation({
    mutationFn: context === 'policy' ? uploadPolicy : uploadBrokerageFirm,
    onSuccess: data => {
      const keys = data.map(d => d.key);
      for (const d of data) {
        fileNamesRef.current.set(d.key, d.fileName);
      }
      setAllComplete(false);
      setPendingCount(prev => prev + keys.length);
      processDocuments(keys);
      posthog.capture('document_prefill_upload_succeeded', {
        context,
        productId,
        policyId: params.policyId,
        brokerageFirmId: params.brokerageFirmId,
        fileCount: keys.length,
        isFirstStep: stepper.isFirstStep,
      });
    },
    onError: () => {
      // Surface upload failures explicitly — previously the mutation silently swallowed
      // errors, which made users think their first upload had worked (it hadn't) and
      // led to confused "I had to upload twice" reports on review environments.
      toast({
        description: t('documents.prefill.uploadError'),
        variant: 'destructive',
      });
      posthog.capture('document_prefill_upload_failed', {
        context,
        productId,
        policyId: params.policyId,
        brokerageFirmId: params.brokerageFirmId,
        isFirstStep: stepper.isFirstStep,
      });
    },
  });

  // Delete an already-uploaded document from the funnel step. Reuses the same hooks
  // as DocumentsUploadPolicyModule / DocumentsUploadBrokerageFirmModule so the delete
  // behaviour (auth, cascades, audit) stays consistent with the dashboard.
  const invalidateDocumentsQuery = useCallback(() => {
    if (context === 'policy' && params.policyId) {
      queryClient.invalidateQueries({
        queryKey: policyControllerListPolicyDocumentsQueryOptions(params.policyId, {
          branchId: params.branchId,
        }).queryKey,
      });
    }
    if (context === 'brokerageFirm' && params.brokerageFirmId) {
      queryClient.invalidateQueries({
        queryKey: brokerageFirmControllerListDocumentsQueryOptions(params.brokerageFirmId).queryKey,
      });
    }
  }, [context, params.policyId, params.branchId, params.brokerageFirmId, queryClient]);

  // Cleanup for the delete-revert flow: prefill wrote values into form fields via
  // AutoFillIndicator, which remembers in sessionStorage (`autofill:*`) that it has
  // already seen a given field so it won't re-apply on subsequent renders. When the
  // source document is deleted the backend reverts those values — but without clearing
  // the sessionStorage flag the UI hint (and re-prefill from a replacement doc) never
  // re-fires. Clear the entire prefix because we don't know which specific entity/field
  // keys belonged to this document; AutoFillIndicator is idempotent on empty fields so
  // this is safe.
  const clearAutofillSessionFlags = useCallback(() => {
    const prefix = 'autofill:';
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(prefix)) sessionStorage.removeItem(key);
    }
  }, []);

  const onDeleteSuccess = useCallback(
    (documentId: string) => {
      dismissDocument(documentId);
      invalidateDocumentsQuery();
      clearAutofillSessionFlags();
      queryClient.invalidateQueries();
      // Funnel forms use both SWR and TanStack Query caches.
      swrMutate(() => true);
    },
    [dismissDocument, invalidateDocumentsQuery, clearAutofillSessionFlags, queryClient],
  );

  const onDeleteError = () =>
    toast({
      description: t('documents.prefill.deleteError'),
      variant: 'destructive',
    });
  const { mutateAsync: deletePolicyDocument } = usePolicyControllerDeletePolicyDocuments({
    mutation: { onError: onDeleteError },
  });
  const { mutateAsync: deleteBrokerageFirmDocument } = useBrokerageFirmControllerDeleteDocuments({
    mutation: { onError: onDeleteError },
  });
  const handleDeleteDocument = async (document: DocumentViewerDocument) => {
    if (context === 'policy' && params.policyId) {
      await deletePolicyDocument({
        policyId: params.policyId,
        params: { ids: [document.id], branchId: params.branchId },
      });
    } else if (context === 'brokerageFirm' && params.brokerageFirmId) {
      await deleteBrokerageFirmDocument({
        brokerageFirmId: params.brokerageFirmId,
        params: { ids: [document.id] },
      });
    } else {
      throw new Error('Missing document owner');
    }
    onDeleteSuccess(document.id);
  };

  const classificationSignature = useMemo(
    () =>
      Array.from(classificationStates.entries())
        .map(([k, s]) => `${k}:${s.status}`)
        .join('|'),
    [classificationStates],
  );
  useEffect(() => {
    if (allComplete) return;
    if (classificationStates.size === 0) return;
    const hasNonTerminal = Array.from(classificationStates.values()).some(
      s => s.status !== 'completed' && s.status !== 'error' && s.status !== 'scan_infected',
    );
    if (!hasNonTerminal) return;
    const remainingInactivityMs = Math.max(
      0,
      CLASSIFICATION_STALE_TIMEOUT_MS - (Date.now() - lastActivityAt),
    );
    const timer = setTimeout(() => {
      setAllComplete(true);
      invalidateDocumentsQuery();
    }, remainingInactivityMs);
    return () => clearTimeout(timer);
  }, [
    classificationSignature,
    classificationStates,
    lastActivityAt,
    allComplete,
    invalidateDocumentsQuery,
  ]);

  if (isConfigLoading || isTenantConfigLoading) {
    return <LoadingCard />;
  }

  // A document is "claimed" by the processing list while it has a non-`completed`
  // classification state — that includes in-flight phases and error states. Any
  // doc claimed here is hidden from the existing-documents list, and conversely
  // any successfully completed doc that's already in `existingDocuments` is
  // hidden from the processing list. Without this split, a window-focus refetch
  // mid-classification would surface the same row in both lists at once.
  const isClaimedByProcessing = (key: string) => {
    const state = classificationStates.get(key);
    return state !== undefined && state.status !== 'completed';
  };

  const renderedExistingDocuments = existingDocuments.filter(doc => !isClaimedByProcessing(doc.id));

  const processingEntries = Array.from(classificationStates.entries()).filter(
    ([key, state]) => state.status !== 'completed' || !existingDocIds.has(key),
  );

  return (
    <Stack gap={6}>
      <FunnelStepHeader
        title={t('documents.prefill.choice.title')}
        subtitle={t('documents.prefill.choice.subtitle')}
      />

      <FunnelWithHelper helper={helper}>
        <Stack>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ChoiceCard
              icon={<Upload />}
              iconVariant="primary"
              title={t('documents.prefill.choice.upload.title')}
              description={t('documents.prefill.choice.upload.description')}
              footer={
                <Group gap={0} className="gap-1.5 text-xs font-medium text-primary">
                  <Zap className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                  {t('documents.prefill.choice.upload.timeSaved')}
                </Group>
              }
              selected={effectiveChoice === 'upload'}
              onClick={() => setChoice('upload')}
            />
            <ChoiceCard
              icon={<ArrowRight />}
              iconVariant="muted"
              title={t('documents.prefill.choice.later.title')}
              description={t('documents.prefill.choice.later.description')}
              footer={
                <div className="text-sm font-medium">
                  {t('documents.prefill.choice.later.followUp')}
                </div>
              }
              selected={effectiveChoice === 'later'}
              disabled={hasUploaded}
              onClick={() => setChoice('later')}
            />
          </div>

          {effectiveChoice === 'upload' ? (
            <Card>
              <CardContent className="flex flex-col gap-3 pt-6">
                <DocumentsCollection
                  groups={getUploadedDocumentGroups({
                    documents: renderedExistingDocuments,
                    categories: documentsLists,
                    translate: documentTranslate,
                  })}
                  upload={{ onDrop: handleUpload, isUploading, enforceAiSizeLimit: true }}
                  onView={selectDocument}
                  classificationCategories={documentsLists}
                  onDelete={displayContext === 'broker' ? handleDeleteDocument : undefined}
                  afterUpload={
                    processingEntries.length > 0 && !allComplete ? (
                      <Stack gap={2}>
                        {processingEntries.map(([key, state]) => (
                          <DocumentProcessingItem
                            key={key}
                            fileName={fileNamesRef.current.get(key) ?? key}
                            state={state}
                            onRemove={() => handleDismissDocument(key)}
                          />
                        ))}
                      </Stack>
                    ) : undefined
                  }
                />

                {documentsLists.length > 0 && (
                  <Stack gap={3} className="pt-2">
                    <Alert variant="info">
                      <Info />
                      <AlertDescription>
                        {t('documents.prefill.choice.optionalHint')}
                      </AlertDescription>
                    </Alert>

                    {prefillChips.length > 0 && (
                      <Stack gap={2}>
                        <Text size="xs" tone="muted" className="uppercase tracking-wide">
                          {t('documents.prefill.choice.prefillGroupLabel')}
                        </Text>
                        <Group align="stretch" wrap gap={2}>
                          {prefillChips.map(({ category }) => {
                            const isMatched = matchedCategories.has(normalizeCategory(category));
                            return (
                              <div
                                key={category}
                                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
                                  isMatched
                                    ? 'border-green-200 bg-green-50 text-green-700'
                                    : 'border-primary/30 bg-white text-foreground'
                                }`}
                              >
                                {isMatched ? (
                                  <CircleCheck className="h-3.5 w-3.5 shrink-0 text-green-600" />
                                ) : (
                                  <Zap className="h-3.5 w-3.5 shrink-0 fill-yellow-400 text-yellow-400" />
                                )}
                                {documentTranslate(`documents.${category}`, {
                                  defaultValue: category,
                                })}
                              </div>
                            );
                          })}
                        </Group>
                      </Stack>
                    )}

                    {otherChips.length > 0 && (
                      <Stack gap={2}>
                        <Text size="xs" tone="muted" className="uppercase tracking-wide">
                          {t('documents.prefill.choice.otherGroupLabel')}
                        </Text>
                        <Group align="stretch" wrap gap={2}>
                          {otherChips.map(({ category }) => {
                            const isMatched = matchedCategories.has(normalizeCategory(category));
                            return (
                              <div
                                key={category}
                                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
                                  isMatched
                                    ? 'border-green-200 bg-green-50 text-green-700'
                                    : 'bg-white text-muted-foreground'
                                }`}
                              >
                                {isMatched ? (
                                  <CircleCheck className="h-3.5 w-3.5 shrink-0 text-green-600" />
                                ) : (
                                  <FileText className="h-3.5 w-3.5 shrink-0" />
                                )}
                                {documentTranslate(`documents.${category}`, {
                                  defaultValue: category,
                                })}
                              </div>
                            );
                          })}
                        </Group>
                      </Stack>
                    )}
                  </Stack>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex items-start gap-3 pt-6">
                <ThemeIcon radius="md" tone="success">
                  <CircleCheck />
                </ThemeIcon>
                <Stack gap={1}>
                  <Text weight="medium">{t('documents.prefill.choice.later.confirmTitle')}</Text>
                  <Text size="sm" tone="muted">
                    {t('documents.prefill.choice.later.confirmDescription')}
                  </Text>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </FunnelWithHelper>

      <StepperFooter
        nextButtonEnabled={!isProcessing}
        onNext={() => {
          posthog.capture('document_prefill_continued', {
            context,
            productId,
            policyId: params.policyId,
            brokerageFirmId: params.brokerageFirmId,
            // The non-upload path is the backend enum value 'later'; report it to
            // analytics as 'manual' (the user-facing "enter manually" choice).
            choice: effectiveChoice === 'later' ? 'manual' : effectiveChoice,
            uploadedCount: existingDocCount,
            isFirstStep: stepper.isFirstStep,
          });
          stepper.next();
        }}
        nextButtonText={t('documents.prefill.continue')}
      />

      <DocumentViewer
        productId={productId}
        displayContext={displayContext}
        document={selectedDocument}
        documents={viewerDocuments}
        onClose={closeViewer}
        onDocumentChange={selectDocument}
        uploadMode="batch"
        documentCustomCategorieMappings={documentsLists}
      />
    </Stack>
  );
}
